import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { getRouteKeyFromSlug, getSlug, stripLocalePrefix, type Locale, TRANSLATED_SLUGS } from "../config";

declare global {
    interface Window {
        Weglot?: {
            initialize: (config: any) => void;
            on: (event: string, callback: (newLang: string, prevLang: string) => void) => void;
            getLanguageName: (langCode: string) => string;
            switchTo: (langCode: string) => void;
            getCurrentLang: () => string;
        };
    }
}

/**
 * Translate a pathname to the target locale with locale prefix.
 *
 * Handles:
 *  - Simple slugs:              /osta            → /en/buy
 *  - Slugs with dynamic params: /osta/senioritabletti → /en/buy/senior-tablet
 *  - Two-segment slugs:         /salasana/vaihda → /en/password/reset
 *  - Locale-prefixed input:     /sv/kop          → /en/buy
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function translatePathname(pathname: string, newLocale: Locale): string {
    // Strip any existing locale prefix to get the bare path
    const bare = stripLocalePrefix(pathname);
    const segments = bare.split("/").filter(Boolean);

    if (segments.length === 0) {
        return newLocale === "fi" ? "/" : `/${newLocale}`;
    }

    // Kill switch: keep Finnish slugs
    if (!TRANSLATED_SLUGS) {
        return newLocale === "fi" ? bare : `/${newLocale}${bare}`;
    }

    // Determine current locale from localStorage (previous lang)
    // Try _prevLang first, but fall back to detecting from the slug itself
    let currentLocale = (localStorage.getItem("_prevLang") || "fi") as Locale;

    // If the first slug doesn't resolve with _prevLang, try "fi" (Finnish slugs are the default)
    const firstSlug = segments[0];
    if (!getRouteKeyFromSlug(firstSlug, currentLocale) && getRouteKeyFromSlug(firstSlug, "fi")) {
        currentLocale = "fi";
    }
    const prefix = newLocale === "fi" ? "" : `/${newLocale}`;

    // Try two-segment slug first (e.g. salasana/vaihda, password/reset)
    if (segments.length >= 2) {
        const twoSegSlug = `${segments[0]}/${segments[1]}`;
        const twoSegKey = getRouteKeyFromSlug(twoSegSlug, currentLocale);
        if (twoSegKey) {
            const newSlug = getSlug(twoSegKey, newLocale);
            return newSlug ? `${prefix}/${newSlug}` : prefix || "/";
        }
    }

    // Try single-segment slug, preserving any trailing dynamic params
    const slug = segments[0];
    const routeKey = getRouteKeyFromSlug(slug, currentLocale);

    if (routeKey) {
        const newSlug = getSlug(routeKey, newLocale);
        const basePath = newSlug ? `${prefix}/${newSlug}` : prefix || "/";
        // Translate trailing params if they're known slugs, otherwise keep as-is
        const trailingParts = segments.slice(1).map((seg) => {
            const segKey = getRouteKeyFromSlug(seg, currentLocale);
            return segKey ? getSlug(segKey, newLocale) : seg;
        });
        const trailing = trailingParts.join("/");
        return trailing ? `${basePath}/${trailing}` : basePath;
    }

    // Unknown slug — keep as-is but add/remove prefix
    return `${prefix}${bare}`;
}

const useWeglotInitializer = () => {
    const location = useLocation();
    const initialized = useRef(false);
    const pathnameRef = useRef(location.pathname);

    useEffect(() => {
        pathnameRef.current = location.pathname;
    }, [location.pathname]);

    useEffect(() => {
        const first = location.pathname.split("/")[1];
        document.documentElement.lang = first === "en" || first === "sv" ? first : "fi";
    }, [location.pathname]);

    // Sync localStorage("language") to match URL prefix on every navigation.
    // Ensures URL and language helpers agree after Back, hard refresh, or direct navigation.
    useEffect(() => {
        if (location.pathname.startsWith("/admin")) return;
        const first = location.pathname.split("/")[1];
        const urlLang: Locale = first === "en" || first === "sv" ? first : "fi";
        const storedLang = localStorage.getItem("language");

        if (storedLang !== urlLang) {
            localStorage.setItem("language", urlLang);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (initialized.current) return;

        const meet = location.pathname.split("/")[1] === "meet";
        const initializedByWeglotConnect = !!document.getElementById("weglot-data");

        if (!meet && initializedByWeglotConnect) {
            initialized.current = true;
            return;
        }

        if (!meet && window.Weglot && typeof window.Weglot.initialize === "function") {
            window.Weglot.initialize({
                api_key: process.env.REACT_APP_WEGLOT_KEY,
                dynamic: "body",
                switchers: [],
            });
            initialized.current = true;

            window.Weglot.on("initialized", () => {
                // In subdirectory mode, Weglot proxy already translated the page
                // server-side based on URL prefix. No switchTo needed on init.
            });

            // localStorage writes for lang are owned by LanguageChangeDialog.applyChange
            // to prevent race conditions with the confirmation dialog.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export default useWeglotInitializer;
